import type {ComponentStageData, ComponentStageHeuristicDefinition, ReadOnlyExtensionAPI} from '../../types'
import {
    updateTargetLayout,
    isSuperGrid,
    isSuperGridCell,
    isSlottedComponent,
    isHeaderContainer,
    isEmptyBox,
    isEmptyRootContainer
} from '../common'
import {getTargetLayoutPointer, getMeasurementsPointer} from '../../conversionDal/utils/pointer'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {OrderPluginAPI} from '../order/types'
import {fractionRowDefinition, minMaxRowDefinition, minContentRowDefinition} from './constants'
import _ from 'lodash'

const updateContainerRows = (
    componentStageData: ComponentStageData,
    rowDefinition: any,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI
) => {
    const {componentPointer, conversionDal, globalContext} = componentStageData
    const {order} = readOnlyExtensionAPI.getPluginsApi(globalContext)
    const orderApi = order as OrderPluginAPI
    let targetRowCount = orderApi.getContainerRowCount(componentPointer)

    if (!targetRowCount) {
        targetRowCount = 1
    }

    const finalRowCount = targetRowCount
    const {containerLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}
    const newRows = Array(finalRowCount).fill(rowDefinition)

    updateTargetLayout(componentStageData, {
        containerLayout: {
            ...containerLayout,
            rows: newRows,
            type: 'GridContainerLayout'
        }
    })
}

const updateContainerRowsAdaptive = (
    componentStageData: ComponentStageData,
    rowDefinition: any,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI
) => {
    const {componentPointer, conversionDal, globalContext} = componentStageData
    const {order} = readOnlyExtensionAPI.getPluginsApi(globalContext)
    const orderApi = order as OrderPluginAPI
    const targetRowCount = orderApi.getContainerRowCount(componentPointer)

    if (targetRowCount === undefined) {
        return
    }

    const finalRowCount = targetRowCount
    const {containerLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer))) ?? {}
    const currentRows = containerLayout?.rows ?? []

    let newRows

    if (finalRowCount > currentRows.length) {
        newRows = [...currentRows, ...Array(finalRowCount - currentRows.length).fill(rowDefinition)]
    } else {
        return
    }

    updateTargetLayout(componentStageData, {
        containerLayout: {
            ...containerLayout,
            rows: newRows,
            type: 'GridContainerLayout'
        }
    })
}

const createMinHeightRowDefinition = (height: number) => ({
    type: 'MinMaxSize',
    min: {
        value: height,
        type: 'spx'
    },
    max: {
        type: 'auto'
    }
})

const updateContainerWithSingleMinHeightRow = (
    componentStageData: ComponentStageData,
    height: number,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI
) => {
    const rowDefinition = createMinHeightRowDefinition(height)
    updateContainerRows(componentStageData, rowDefinition, readOnlyExtensionAPI)
}

const isEmptySmallBox = (componentStageData: ComponentStageData) => {
    const {componentPointer, initialSnapshot} = componentStageData
    const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
    const originalMeasurements = initialSnapshot.getValue(componentMeasurementsPointer)

    return isEmptyBox(componentStageData) && originalMeasurements.height < 101
}

const isContainer = ({componentPointer, conversionDal}: ComponentStageData) =>
    conversionDal.get(componentPointer).type === 'Container'

export const rowHeuristicsFlush: ComponentStageHeuristicDefinition[] = [
    {
        predicate: isSlottedComponent,
        apply: () => {},
        heuristicCode: 'SLOTTED COMPONENT SKIP'
    },
    {
        predicate: isHeaderContainer,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerWithSingleMinHeightRow(componentStageData, 70, readOnlyExtensionAPI!)
        },
        heuristicCode: 'HEADER ROW FLUSH'
    },
    {
        predicate: isEmptyRootContainer,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerWithSingleMinHeightRow(componentStageData, 200, readOnlyExtensionAPI!)
        },
        heuristicCode: 'EMPTY ROOT CONTAINER ROWS FLUSH'
    },
    {
        predicate: isEmptySmallBox,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerWithSingleMinHeightRow(componentStageData, 100, readOnlyExtensionAPI!)
        },
        heuristicCode: 'EMPTY SMALL BOX ROWS FLUSH'
    },
    {
        predicate: isEmptyBox,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerWithSingleMinHeightRow(componentStageData, 200, readOnlyExtensionAPI!)
        },
        heuristicCode: 'EMPTY LARGE BOX ROWS FLUSH'
    },
    {
        predicate: isSuperGrid,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerRows(componentStageData, minMaxRowDefinition, readOnlyExtensionAPI!)
        },
        heuristicCode: 'SUPERGRID ROWS FLUSH'
    },
    {
        predicate: isSuperGridCell,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerRows(componentStageData, fractionRowDefinition, readOnlyExtensionAPI!)
        },
        heuristicCode: 'SUPERGRIDCELL ROWS FLUSH'
    },
    {
        predicate: isContainer,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerRows(componentStageData, minContentRowDefinition, readOnlyExtensionAPI!)
        },
        heuristicCode: 'DEFAULT ROWS FLUSH'
    }
]

export const rowHeuristicsAdaptive: ComponentStageHeuristicDefinition[] = [
    {
        predicate: isSlottedComponent,
        apply: () => {},
        heuristicCode: 'SLOTTED COMPONENT SKIP'
    },
    {
        predicate: isSuperGrid,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerRowsAdaptive(componentStageData, minMaxRowDefinition, readOnlyExtensionAPI!)
        },
        heuristicCode: 'SUPERGRID ROWS ADAPTIVE'
    },
    {
        predicate: isSuperGridCell,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerRowsAdaptive(componentStageData, fractionRowDefinition, readOnlyExtensionAPI!)
        },
        heuristicCode: 'SUPERGRIDCELL ROWS ADAPTIVE'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const targetLayout = initialSnapshot.getValue(getTargetLayoutPointer(componentPointer))
            return isEmptySmallBox(componentStageData) && _.isEmpty(targetLayout?.containerLayout)
        },
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerWithSingleMinHeightRow(componentStageData, 100, readOnlyExtensionAPI!)
        },
        heuristicCode: 'EMPTY SMALL BOX ROWS ADAPTIVE'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const targetLayout = initialSnapshot.getValue(getTargetLayoutPointer(componentPointer))
            return isEmptyBox(componentStageData) && _.isEmpty(targetLayout?.containerLayout)
        },
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerWithSingleMinHeightRow(componentStageData, 200, readOnlyExtensionAPI!)
        },
        heuristicCode: 'EMPTY LARGE BOX ROWS ADAPTIVE'
    },
    {
        predicate: isContainer,
        apply: (componentStageData: ComponentStageData, _contextHelper, readOnlyExtensionAPI) => {
            updateContainerRowsAdaptive(componentStageData, minContentRowDefinition, readOnlyExtensionAPI!)
        },
        heuristicCode: 'DEFAULT ROWS ADAPTIVE'
    }
]
